/**
 * The dialogs a PO line's detail modal can open: acknowledge, propose, set
 * dates, hold, and the concession / production-permit request.
 *
 * Lifted out of POLineDetailModal at gate round 1 (comments-C3). It is wiring —
 * which line, which flag, which handler — and keeping it in the modal had that
 * file over helix-code's 500-line cap, which this item's own work pushed it past
 * (517 at the parent commit, 524 with this commit's own additions, 487 after).
 *
 * Nothing here decides anything: every rule about WHEN a control appears is
 * still in the modal and its footer, where the line, the user and the tenant
 * switches are. This component only renders what the modal says is open.
 */
import { AcceptDateModal, ProposeChangeModal } from './AcknowledgmentWorkflowModals';
import { HoldReasonModal } from './HoldReasonModal';
import { RaiseConcessionFromLineModal } from './RaiseConcessionFromLineModal';
import { SetDatesModal, type SetDatesPayload } from './SetDatesModal';
import type { HoldNextAction } from './HoldNextActionChoice';
import type { PurchaseOrderLine } from '../../lib/api';
import type { ConcessionRequestType } from '../../lib/api/types/quality';
import type { WorkflowActionPayload } from './AcknowledgmentSharedUtils';

// The payload type lives with the sender (usePOLineDetail), imported below.

interface POLineDetailDialogsProps {
  line: PurchaseOrderLine;
  userType?: 'customer' | 'supplier';
  /**
   * The side placing a hold, when the viewer IS one. `userType` folds the platform
   * operator into 'customer', which is what made an operator's "With me" record a
   * handover to the customer (F-J, blind stage 2, round 2).
   */
  placerSide?: 'customer' | 'supplier';
  acceptOpen: boolean;
  proposeOpen: boolean;
  setDatesOpen: boolean;
  holdOpen: boolean;
  deviationRequestType: ConcessionRequestType | null;
  onCloseAccept: () => void;
  onClosePropose: () => void;
  onCloseSetDates: () => void;
  onCloseHold: () => void;
  onCloseDeviation: () => void;
  onWorkflowAction: (data: WorkflowActionPayload) => Promise<void>;
  onSetDates: (payload: SetDatesPayload) => Promise<void>;
  onHold: (reason: string, nextAction?: HoldNextAction) => Promise<void>;
  onDeviationSuccess: () => Promise<void>;
}

export function POLineDetailDialogs({
  line,
  userType,
  placerSide,
  acceptOpen,
  proposeOpen,
  setDatesOpen,
  holdOpen,
  deviationRequestType,
  onCloseAccept,
  onClosePropose,
  onCloseSetDates,
  onCloseHold,
  onCloseDeviation,
  onWorkflowAction,
  onSetDates,
  onHold,
  onDeviationSuccess,
}: POLineDetailDialogsProps) {
  return (
    <>
      {/* CR-4729 rework: pass the supplier-proposed promise date so the modal
          can render "From <delivery_date> to <proposedDate>" instead of just
          the existing date. */}
      <AcceptDateModal
        isOpen={acceptOpen}
        onClose={onCloseAccept}
        lineId={line.id}
        lineNumber={line.line_number}
        partNumber={line.part_number}
        orderNumber={line.po_number}
        currentDeliveryDate={line.delivery_date}
        proposedDate={line.supplier_proposed_date || line.supplier_promise_date || undefined}
        onSubmit={onWorkflowAction}
      />
      <ProposeChangeModal
        isOpen={proposeOpen}
        onClose={onClosePropose}
        lineId={line.id}
        lineNumber={line.line_number}
        partNumber={line.part_number}
        orderNumber={line.po_number}
        currentDeliveryDate={line.delivery_date}
        currentQuantity={line.quantity}
        currentUnitPrice={line.unit_price}
        onSubmit={onWorkflowAction}
        userType={userType}
        quantityProposalsHidden={line.quantity_proposals_hidden}
      />
      <SetDatesModal
        isOpen={setDatesOpen}
        onClose={onCloseSetDates}
        lineNumber={line.line_number}
        partNumber={line.part_number}
        orderNumber={line.po_number}
        /* The payload's pinned keys: delivery_date IS the Customer Required
           Date and original_promise_date IS the ODD (DATE-FIELD-CANON).

           THE ODD BOX SEEDS FROM THE COLUMN THE WRITE TOUCHES, and that is
           `original_promise_date` — the raw original_required_date. It used to
           seed from `odd_date`, so an empty ODD showed the Detail tab's
           fallback rather than a blank box; two things were wrong with that
           (F19, gate round 1). `odd_date` is GRAIN-AWARE and answers
           customer_required_date on a schedule-grain tenant, while
           set_dates_at_new always writes the line's own column — so the box
           showed a value the write did not control. And a pre-filled box cannot
           be empty, which is now load-bearing: an empty ODD is what tells the
           modal to say that moving the CRD will pin the anchor where it stands
           (F3). Blank is the honest state, and the modal explains it. */
        currentCustomerRequiredDate={line.delivery_date}
        currentOriginalRequiredDate={line.original_promise_date ?? null}
        currentSupplierPromiseDate={line.supplier_promise_date}
        onSubmit={onSetDates}
      />
      <HoldReasonModal
        isOpen={holdOpen}
        onClose={onCloseHold}
        onSubmit={onHold}
        lineNumber={line.line_number}
        partNumber={line.part_number}
        placerSide={placerSide}
      />
      {/* CR-5551: PE/CE modal — single component, requestType drives copy. */}
      {deviationRequestType && (
        <RaiseConcessionFromLineModal
          isOpen={true}
          onClose={onCloseDeviation}
          line={line}
          requestType={deviationRequestType}
          onSuccess={onDeviationSuccess}
        />
      )}
    </>
  );
}
